/**
 * rod-footer.js (WP-8, SPEC 5.14)
 * - Footer link columns become accordions on phones (< 750). Buttons carry aria-expanded,
 *   several may be open at once, and the panels are always open from 750 up.
 * - Newsletter: the consent checkbox IS the `contact[accepts_marketing]` field and is `required`,
 *   so an unticked submit is blocked natively when this module never runs. Here we only replace
 *   the native validation bubbles with the Turkish inline messages; `required` and `name` stay.
 *   An invalid form is stopped at the submit button's CLICK so that no `submit` event is ever
 *   generated and Shopify's hCaptcha is never armed by a submission that is not sent.
 * - "Çerez Tercihleri" reopens the Shopify customer-privacy preferences.
 */

const PHONE_QUERY = '(max-width: 749px)';
const PRIVACY_BANNER_SRC = 'https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js';

const MSG_CONSENT = 'Abone olmak için ticari elektronik ileti onayını işaretleyin.';
const MSG_EMAIL = 'Lütfen geçerli bir e-posta adresi girin.';

/* ------------------------------------------------------------------ accordions
 * The column heading ships as a plain <span class="rod-ftr__acc" data-rod-panel="…">. Below 750 it is
 * upgraded to a real <button type="button" aria-expanded aria-controls>; from 750 up (and whenever
 * this module does not run) it stays a span, so no screen reader ever meets a "button, expanded"
 * that cannot be focused or activated, and every panel is open.
 */

function panelOf(el) {
  const id = el.dataset.rodPanel || el.getAttribute('aria-controls') || '';
  return id ? document.getElementById(id) : null;
}

function setExpanded(button, open) {
  button.setAttribute('aria-expanded', open ? 'true' : 'false');
  const panel = panelOf(button);
  if (panel) panel.hidden = !open;
}

/** Swap one element for another tag, keeping class, children and the panel reference. */
function swapTo(el, tagName) {
  const next = document.createElement(tagName);
  next.className = el.className;
  next.dataset.rodPanel = el.dataset.rodPanel || el.getAttribute('aria-controls') || '';
  while (el.firstChild) next.appendChild(el.firstChild);
  el.replaceWith(next);
  return next;
}

function toButton(el) {
  const button = swapTo(el, 'button');
  button.type = 'button';
  if (button.dataset.rodPanel) button.setAttribute('aria-controls', button.dataset.rodPanel);
  setExpanded(button, false);
  return button;
}

function toStatic(el) {
  const span = swapTo(el, 'span');
  const panel = panelOf(span);
  if (panel) panel.hidden = false;
  return span;
}

function initAccordions(root) {
  if (!root.querySelector('.rod-ftr__acc')) return;

  const mq = window.matchMedia(PHONE_QUERY);

  const apply = () => {
    const phone = mq.matches;
    for (const el of Array.from(root.querySelectorAll('.rod-ftr__acc'))) {
      const isButton = el.tagName === 'BUTTON';
      if (phone && !isButton) toButton(el);
      else if (!phone && isButton) toStatic(el);
      else if (!phone) {
        const panel = panelOf(el);
        if (panel) panel.hidden = false;
      }
    }
  };

  // Delegated, so the node swaps above never lose their handler.
  root.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('button.rod-ftr__acc') : null;
    if (!button || !root.contains(button)) return;
    setExpanded(button, button.getAttribute('aria-expanded') !== 'true');
  });

  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', apply);
  else if (typeof mq.addListener === 'function') mq.addListener(apply);
  apply();
}

/* ------------------------------------------------------------------ newsletter */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function showMessage(field, message) {
  const id = field?.getAttribute('aria-describedby');
  const box = id ? document.getElementById(id) : null;
  if (box) {
    box.textContent = message;
    box.hidden = !message;
  }
  if (field) {
    if (message) field.setAttribute('aria-invalid', 'true');
    else field.removeAttribute('aria-invalid');
  }
}

/**
 * Shows the Turkish messages and returns the first invalid field (null when the form may be sent).
 * `form.noValidate` switches off validation AT SUBMIT TIME only: every field still keeps a live
 * ValidityState, so we consult the browser's own `type="email"` check (stricter than any regex we
 * would write, e.g. for non-ASCII addresses) on top of the regex, and keep our own message text.
 */
function validateNewsletter(email, consent) {
  let firstInvalid = null;

  if (email) {
    const value = email.value.trim();
    const nativeOk = email.validity ? email.validity.valid : true;
    const ok = value !== '' && EMAIL_RE.test(value) && nativeOk;
    showMessage(email, ok ? '' : MSG_EMAIL);
    if (!ok) firstInvalid = email;
  }

  if (consent) {
    const ok = consent.checked;
    showMessage(consent, ok ? '' : MSG_CONSENT);
    if (!ok && !firstInvalid) firstInvalid = consent;
  }

  return firstInvalid;
}

function initNewsletter(form) {
  const email = form.querySelector('input[type="email"]');
  const consent = form.querySelector('input[name="contact[accepts_marketing]"]');
  if (!email && !consent) return;

  // Progressive enhancement only: the native `required` attributes stay on the fields.
  form.noValidate = true;

  if (email) email.addEventListener('input', () => showMessage(email, ''));
  if (consent) consent.addEventListener('change', () => showMessage(consent, ''));

  const block = (event) => {
    const firstInvalid = validateNewsletter(email, consent);
    if (!firstInvalid) return false;
    event.preventDefault();
    firstInvalid.focus();
    return true;
  };

  /*
   * An invalid form is blocked on the BUTTON CLICK, not on the submit event. Shopify's spam
   * protection listens for `submit` on this form as well, and `preventDefault()` does not stop the
   * other listeners on the same event: it processed every blocked submit and escalated its hCaptcha
   * to a full image challenge that covered the footer (verified live 2026-09-16) although nothing
   * was ever POSTed. Cancelling the click means no `submit` event is generated at all. Implicit
   * submission (Enter in a field) also runs through the default button's click, so the keyboard
   * path is covered; a valid click is untouched and Shopify's invisible check still runs on the
   * real POST. (The invisible widget itself is injected by Shopify on first input into the form;
   * that is platform behaviour and harmless - it renders 0px high.)
   */
  const submitButton = form.querySelector('button[type="submit"], input[type="submit"], button:not([type])');
  if (submitButton) submitButton.addEventListener('click', block);

  /*
   * Backstop for a submit we did not originate (scripted `form.submit()`-style flows, or a browser
   * that submits without clicking the default button). Capture phase on the document runs before
   * any listener bound to the form itself, so stopping it here also keeps it away from Shopify's
   * handler. Scoped to this form only.
   */
  document.addEventListener(
    'submit',
    (event) => {
      if (event.target !== form) return;
      if (block(event)) event.stopImmediatePropagation();
    },
    true
  );
}

/* ------------------------------------------------------------------ cookie preferences */

function privacyPolicyUrl() {
  const fromConfig = window.Rod?.config?.policies?.privacy;
  return typeof fromConfig === 'string' && fromConfig ? fromConfig : '/policies/privacy-policy';
}

function loadPrivacyBanner() {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PRIVACY_BANNER_SRC}"]`);
    if (existing) {
      if (window.privacyBanner) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('privacy banner')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = PRIVACY_BANNER_SRC;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('privacy banner')), { once: true });
    document.head.appendChild(script);
  });
}

/**
 * The Shopify privacy script can load and expose showPreferences() while no consent banner is
 * configured for the shop; the dialog then renders 0x0 and the visitor gets no feedback at all.
 * So we do not trust the API call: we wait for a preferences surface that is actually visible and
 * otherwise take the documented fallback (the privacy policy). The button is never dead.
 */
const PC_NODES = '[id^="shopify-pc"]';
const PC_PREFS = '#shopify-pc__prefs, [id^="shopify-pc"][id*="prefs"]';

function visibleNodes(selector) {
  const out = new Set();
  for (const node of document.querySelectorAll(selector)) {
    const rect = node.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) out.add(node);
  }
  return out;
}

/** Opened = the preferences surface is visible, or any consent surface appeared after the click. */
function prefsOpened(before) {
  if (visibleNodes(PC_PREFS).size > 0) return true;
  for (const node of visibleNodes(PC_NODES)) if (!before.has(node)) return true;
  return false;
}

function waitForPrefs(before, timeout = 1500) {
  return new Promise((resolve) => {
    const deadline = Date.now() + timeout;
    const tick = () => {
      if (prefsOpened(before)) return resolve(true);
      if (Date.now() >= deadline) return resolve(false);
      setTimeout(tick, 100);
    };
    tick();
  });
}

async function openPreferences() {
  // Kendi çerez bandımız (rod-consent) varsa önce o açılır: izni toplayan
  // yüzey odur. Shopify'ın hazır bandı yalnızca yöneticiden açılabildiği için
  // bu mağazada kapalı; aşağıdaki yol yedektir.
  if (typeof window.rodConsent?.open === 'function') {
    window.rodConsent.open();
    return true;
  }
  const before = visibleNodes(PC_NODES);
  if (!window.privacyBanner?.showPreferences) {
    try {
      await loadPrivacyBanner();
    } catch {
      return false; /* the banner script is unavailable: fall through to the policy page */
    }
  }
  if (!window.privacyBanner?.showPreferences) return false;
  try {
    window.privacyBanner.showPreferences();
  } catch {
    return false;
  }
  return waitForPrefs(before);
}

function initCookiePrefs(root) {
  const button = root.querySelector('[data-rod-cookie-prefs]');
  if (!button) return;
  button.addEventListener('click', async () => {
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    let opened = false;
    try {
      opened = await openPreferences();
    } finally {
      button.removeAttribute('aria-busy');
    }
    if (!opened) window.location.assign(privacyPolicyUrl());
  });
}

/* ------------------------------------------------------------------ boot */

function init() {
  for (const root of document.querySelectorAll('.rod-ftr')) {
    if (root.dataset.rodFtrReady === '1') continue;
    root.dataset.rodFtrReady = '1';
    initAccordions(root);
    initCookiePrefs(root);
  }
  for (const form of document.querySelectorAll('form[data-rod-newsletter]')) {
    if (form.dataset.rodNewsletterReady === '1') continue;
    form.dataset.rodNewsletterReady = '1';
    initNewsletter(form);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.classList?.contains('rod-ftr')) init();
});
